import React, { useState, useEffect, useCallback } from 'react';
import {
  CreditCard, Check, ChevronDown, ChevronUp, Zap, Users, BookOpen,
  BarChart2, AlertCircle, Loader2, ArrowUpRight, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';
import { useAuth } from '../../contexts/AuthContext';

const fmt = (n) => n === 0 ? 'Free' : `₦${Number(n).toLocaleString()}`;
const pct = (used, limit) => Math.min(100, Math.round((used / limit) * 100));

const PLAN_STYLES = {
  gray:   { card: 'border-slate-200 bg-slate-50', badge: 'bg-slate-100 text-slate-600', btn: 'bg-slate-600 hover:bg-slate-700', bar: 'bg-slate-400' },
  blue:   { card: 'border-blue-200 bg-blue-50',   badge: 'bg-blue-100 text-blue-700',   btn: 'bg-blue-600 hover:bg-blue-700',   bar: 'bg-blue-500' },
  purple: { card: 'border-purple-200 bg-purple-50', badge: 'bg-purple-100 text-purple-700', btn: 'bg-purple-600 hover:bg-purple-700', bar: 'bg-purple-500' },
  green:  { card: 'border-emerald-200 bg-emerald-50', badge: 'bg-emerald-100 text-emerald-700', btn: 'bg-emerald-600 hover:bg-emerald-700', bar: 'bg-emerald-500' },
};

const GW_OPTIONS = [
  { id: 'paystack',    label: 'Paystack',    color: '#00C3F7' },
  { id: 'flutterwave', label: 'Flutterwave', color: '#F5A623' },
];

export default function SubscriptionPage() {
  const { school, updateSchool } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedPlan, setExpandedPlan] = useState(null);
  const [showPayModal, setShowPayModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('term');
  const [gateway, setGateway] = useState('paystack');
  const [initiating, setInitiating] = useState(false);
  const [gatewayInfo, setGatewayInfo] = useState({ active_gateways: ['paystack', 'flutterwave'] });

  const fetchData = useCallback(async () => {
    try {
      const [planRes, gwRes] = await Promise.all([
        api.get('/subscriptions/my-plan'),
        api.get('/subscriptions/gateway-info'),
      ]);
      setData(planRes.data);
      setGatewayInfo(gwRes.data || {});
    } catch {
      toast.error('Failed to load subscription info');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openUpgrade = (plan) => {
    setSelectedPlan(plan);
    setBillingCycle('term');
    const activeGws = gatewayInfo.active_gateways || ['paystack', 'flutterwave'];
    setGateway(activeGws[0] || 'paystack');
    setShowPayModal(true);
  };

  const handlePay = async () => {
    if (!selectedPlan) return;
    setInitiating(true);
    try {
      const callbackUrl = `${window.location.origin}/payment/callback`;
      const res = await api.post('/subscriptions/initialize', {
        plan_id: selectedPlan.id,
        billing_cycle: billingCycle,
        gateway,
        callback_url: callbackUrl,
      });
      const { checkout_url } = res.data;
      window.location.href = checkout_url;
    } catch (err) {
      toast.error(err?.detail || 'Could not initiate payment. Check gateway configuration.');
      setInitiating(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
    </div>
  );

  const { plan, subscription, usage, available_plans = [], subscription_status, days_remaining, grace_days_remaining, grace_period_days } = data || {};
  const currentPlanId = plan?.id;
  const studentUsePct = plan ? pct(usage?.students || 0, plan.student_limit) : 0;
  const planStyle = PLAN_STYLES[plan?.color] || PLAN_STYLES.blue;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Manage Subscription</h1>
          <p className="text-sm text-slate-500 mt-0.5">View your current plan, usage, and upgrade options</p>
        </div>
        <button onClick={fetchData} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Current Plan Card */}
      <div className={`rounded-xl border-2 ${planStyle.card} p-6`} data-testid="current-plan-card">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <CreditCard className="w-5 h-5 text-slate-500" />
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Current Plan</span>
            </div>
            <h2 className="text-2xl font-bold text-slate-900">{plan?.name || 'Free Trial'}</h2>
            <p className="text-sm text-slate-500 mt-1">{plan?.description}</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-slate-900">
              {plan ? fmt(plan.term_price || plan.monthly_price || 0) : 'Free'}
            </p>
            {(plan?.term_price || plan?.monthly_price || 0) > 0 && (
              <p className="text-xs text-slate-400">per term ({plan?.term_days || 90} days)</p>
            )}
            {school?.subscription_expires_at && (
              <p className="text-xs text-slate-400 mt-1">
                Expires: {new Date(school.subscription_expires_at).toLocaleDateString('en-NG', { day:'numeric', month:'short', year:'numeric' })}
              </p>
            )}
            {days_remaining !== null && days_remaining !== undefined && (
              <p className={`text-xs font-semibold mt-0.5 ${days_remaining <= 7 ? 'text-red-600' : days_remaining <= 30 ? 'text-amber-500' : 'text-emerald-600'}`}>
                {days_remaining === 0 ? 'Expired' : `${days_remaining} day(s) remaining`}
              </p>
            )}
            {subscription_status === 'grace' && (
              <span className="inline-flex items-center gap-1 mt-1 text-xs font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full" data-testid="grace-period-badge">
                Grace Period · {grace_days_remaining} day(s) left
              </span>
            )}
            {subscription_status === 'active' && (
              <span className="inline-block mt-1 text-xs font-semibold text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full">Active</span>
            )}
          </div>
        </div>

        {/* Usage */}
        <div className="mt-6 grid grid-cols-2 gap-4">
          <div className="bg-white/70 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5 text-xs font-medium text-slate-600">
                <Users className="w-3.5 h-3.5" /> Students
              </div>
              <span className="text-xs font-semibold text-slate-800">
                {usage?.students || 0} / {plan?.student_limit?.toLocaleString() || '∞'}
              </span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className={`h-full ${planStyle.bar} rounded-full transition-all`} style={{ width: `${studentUsePct}%` }} />
            </div>
            {studentUsePct > 80 && (
              <p className="text-[11px] text-amber-600 mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> Approaching limit
              </p>
            )}
          </div>
          <div className="bg-white/70 rounded-xl p-4">
            <div className="flex items-center gap-1.5 text-xs font-medium text-slate-600 mb-2">
              <BookOpen className="w-3.5 h-3.5" /> Modules Included
            </div>
            <p className="text-2xl font-bold text-slate-900">{(plan?.modules || []).length}</p>
            <p className="text-xs text-slate-400">of all available modules</p>
          </div>
        </div>

        {/* Included modules */}
        {plan?.modules?.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-1.5">
            {plan.modules.map(m => (
              <span key={m} className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${planStyle.badge}`}>
                {m.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Available Plans */}
      <div>
        <h2 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">Available Plans</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {available_plans.map(p => {
            const style = PLAN_STYLES[p.color] || PLAN_STYLES.blue;
            const isCurrent = p.id === currentPlanId;
            const isExpanded = expandedPlan === p.id;
            return (
              <div key={p.id} className={`bg-white rounded-xl border ${isCurrent ? 'border-indigo-400 ring-2 ring-indigo-100' : 'border-slate-200'} shadow-sm overflow-hidden`}
                data-testid={`available-plan-${p.name.toLowerCase()}`}>
                <div className={`px-5 py-4 ${style.card}`}>
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-slate-900">{p.name}</h3>
                    {isCurrent && <span className="text-[11px] font-semibold bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">Current</span>}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{p.description}</p>
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="text-xl font-bold text-slate-900">{fmt(p.term_price || p.monthly_price || 0)}</span>
                    {(p.term_price || p.monthly_price || 0) > 0 && <span className="text-xs text-slate-400">/term</span>}
                  </div>
                  {(p.session_price || p.yearly_price || 0) > 0 && (
                    <p className="text-xs text-slate-500 mt-0.5">{fmt(p.session_price || p.yearly_price || 0)}/session</p>
                  )}
                </div>
                <div className="px-5 py-4 space-y-2">
                  <div className="flex items-center gap-1.5 text-xs text-slate-600">
                    <Users className="w-3.5 h-3.5 text-slate-400" />
                    Up to {p.student_limit.toLocaleString()} students
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-slate-600">
                    <BarChart2 className="w-3.5 h-3.5 text-slate-400" />
                    {(p.modules || []).length} modules
                  </div>

                  <button onClick={() => setExpandedPlan(isExpanded ? null : p.id)}
                    className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700">
                    {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    {isExpanded ? 'Hide' : 'See'} all modules
                  </button>

                  {isExpanded && (
                    <div className="border-t border-slate-100 pt-2 space-y-1 max-h-40 overflow-y-auto">
                      {(p.modules || []).map(m => (
                        <div key={m} className="flex items-center gap-1.5 text-xs text-slate-600">
                          <Check className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                          {m.replace(/_/g, ' ')}
                        </div>
                      ))}
                    </div>
                  )}

                  {!isCurrent && (p.term_price || p.monthly_price || 0) > 0 && (
                    <button onClick={() => openUpgrade(p)}
                      className={`w-full mt-2 flex items-center justify-center gap-1.5 py-2 text-xs font-semibold text-white rounded-lg transition-colors ${style.btn}`}
                      data-testid={`upgrade-to-${p.name.toLowerCase()}-btn`}>
                      <Zap className="w-3.5 h-3.5" /> Subscribe to {p.name}
                    </button>
                  )}
                  {!isCurrent && (p.term_price || p.monthly_price || 0) === 0 && (
                    <p className="text-xs text-center text-slate-400 pt-1">Downgrade via support</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Payment Modal */}
      {showPayModal && selectedPlan && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="payment-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-bold text-slate-900">Subscribe to {selectedPlan.name}</h2>
              <button onClick={() => setShowPayModal(false)} className="text-slate-400 hover:text-slate-600 text-xl leading-none">×</button>
            </div>

            {/* Billing Cycle */}
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-2">Billing Period</label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  {
                    id: 'term',
                    label: 'Per Term',
                    price: selectedPlan.term_price || selectedPlan.monthly_price || 0,
                    note: `${selectedPlan.term_days || 90} days`,
                  },
                  {
                    id: 'session',
                    label: 'Full Session',
                    price: selectedPlan.session_price || selectedPlan.yearly_price || 0,
                    note: `${selectedPlan.session_days || 270} days`,
                  },
                ].map(opt => (
                  <button key={opt.id} onClick={() => setBillingCycle(opt.id)}
                    className={`p-3 rounded-xl border text-left transition-colors ${billingCycle === opt.id ? 'border-indigo-400 bg-indigo-50' : 'border-slate-200 hover:bg-slate-50'}`}
                    data-testid={`billing-${opt.id}-btn`}>
                    <p className="text-xs font-semibold text-slate-800">{opt.label}</p>
                    <p className="text-sm font-bold text-slate-900 mt-0.5">{fmt(opt.price)}</p>
                    <span className="text-[11px] text-slate-400">{opt.note}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Gateway Selection */}
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-2">Pay With</label>
              <div className="grid grid-cols-2 gap-2">
                {GW_OPTIONS.filter(gw => (gatewayInfo.active_gateways || []).includes(gw.id)).map(gw => (
                  <button key={gw.id} onClick={() => setGateway(gw.id)}
                    className={`p-3 rounded-xl border flex items-center gap-2 transition-colors ${gateway === gw.id ? 'border-indigo-400 bg-indigo-50' : 'border-slate-200 hover:bg-slate-50'}`}
                    data-testid={`gateway-${gw.id}-btn`}>
                    <div className="w-3 h-3 rounded-full" style={{ background: gw.color }} />
                    <span className="text-sm font-medium text-slate-800">{gw.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Summary */}
            {(() => {
              const price = billingCycle === 'session'
                ? (selectedPlan.session_price || selectedPlan.yearly_price || 0)
                : (selectedPlan.term_price || selectedPlan.monthly_price || 0);
              const days = billingCycle === 'session'
                ? (selectedPlan.session_days || 270)
                : (selectedPlan.term_days || 90);
              return (
                <>
                  <div className="bg-slate-50 rounded-xl p-4 text-sm">
                    <div className="flex justify-between text-slate-600 mb-1">
                      <span>{selectedPlan.name} · {billingCycle === 'session' ? 'Full Session' : 'Per Term'}</span>
                      <span className="font-semibold">{fmt(price)}</span>
                    </div>
                    <div className="flex justify-between text-xs text-slate-400">
                      <span>Access for {days} days</span>
                      <span>NGN</span>
                    </div>
                  </div>
                  <button onClick={handlePay} disabled={initiating}
                    className="w-full flex items-center justify-center gap-2 py-3 bg-indigo-600 text-white text-sm font-semibold rounded-xl hover:bg-indigo-700 transition-colors disabled:opacity-60"
                    data-testid="confirm-payment-btn">
                    {initiating ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUpRight className="w-4 h-4" />}
                    {initiating ? 'Redirecting...' : `Pay ${fmt(price)}`}
                  </button>
                </>
              );
            })()}
            <p className="text-[11px] text-center text-slate-400">You will be redirected to a secure payment page</p>
          </div>
        </div>
      )}
    </div>
  );
}

